import TemplateItems from './TemplateItems'
import ToggleCatalogBrowserButton from "../ItemCatalog/ToggleCatalogBrowserButton"
import { useContext, useEffect, useState } from 'react'
import TemplateContext from './TemplateContext'
import { Form, Input, Popconfirm, Skeleton, Space, Tabs, Typography } from 'antd'
import RichTextEditor from '../RichTextEditor/RichTextEditor'
import { useItemCategories } from '../ItemCatalog/apiQueries'
import MultiSelect from '../common/FormControls/MultiSelect'
import { faClone, faTrash, faTrashArrowUp } from '@fortawesome/pro-solid-svg-icons'
import CopyTemplateModal from './CopyTemplateModal'
import { buildStateForSave, copyTemplate, hasChanges } from './templateModel'
import { useDeleteTemplateMutation, useRestoreTemplateMutation, useSaveTemplateMutation } from './templateQueries'
import IconAndTextButton from '../IconAndTextButton'
import VSpace from '../common/VSpace'

const { Text } = Typography

export type Props = {
  onChange: (hasChange: boolean) => void
  onSave: (id: number) => void
  onDelete: () => void
  onRestore: () => void
  saveCounter: number
}
const TemplateForm = ({ onChange, onSave, onDelete, onRestore, saveCounter }: Props) => {
  const { state, dispatch, serverTemplate, isLoading, isTemplateDeleted } = useContext(TemplateContext)
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false)
  const categoriesQuery = useItemCategories(true)
  const [form] = Form.useForm()

  useEffect(() => {
    if (saveCounter > 0) {
      saveTemplateMutation.mutate({ id: serverTemplate.id, changeSet: buildStateForSave(state) })
    }
  }, [saveCounter])

  const saveTemplateMutation = useSaveTemplateMutation((id) => {
    dispatch({ type: 'TEMPLATE_SAVED' })
    onSave(id)
  })

  const deleteTemplateMutation = useDeleteTemplateMutation(onDelete)
  const restoreTemplateMutation = useRestoreTemplateMutation(onRestore)

  const hasStateChanges = hasChanges(state, serverTemplate.id)
  useEffect(() => {
    onChange(hasStateChanges)
  }, [hasStateChanges])

  useEffect(() => {
    dispatch({ type: 'TEMPLATE_ID_CHANGED' })
  }, [serverTemplate.id])

  const templateName = state.name ?? serverTemplate.name
  const customerNotes = state.workareaCustomerAndInvoiceNotes ?? serverTemplate.workareaCustomerAndInvoiceNotes ?? ''
  const invoiceDescription = state.workareaInvoiceDescription ?? serverTemplate.workareaInvoiceDescription ?? ''
  const internalNotes = state.workareaInternalNotes ?? serverTemplate.workareaInternalNotes

  if (isLoading) {
    return <Skeleton active />
  }

  return <>
    <Form layout="vertical" form={form}>
      <Form.Item label="Template Name">
        <Input placeholder="Template Name" value={templateName} onChange={e => dispatch({ type: 'NAME_UPDATED', value: e.target.value })} status={templateName ? undefined : 'error'} disabled={isTemplateDeleted} maxLength={150} />
      </Form.Item>
      <Form.Item>
        <Space>
          <IconAndTextButton
            type="primary"
            size="large"
            disabled={hasStateChanges || isTemplateDeleted}
            onClick={() => setIsCopyModalOpen(true)}
            icon={faClone}
            text="Copy Template"
          />
          <Popconfirm title="Are you sure you want to delete this template?" onConfirm={() => deleteTemplateMutation.mutate(serverTemplate.id)} disabled={serverTemplate.id === 0 || isTemplateDeleted}>
            <IconAndTextButton
              type="primary"
              size="large"
              danger
              disabled={serverTemplate.id === 0 || isTemplateDeleted}
              icon={faTrash}
              text="Remove Template"
            />
          </Popconfirm>
          {isTemplateDeleted && <IconAndTextButton
            type="primary"
            size="large"
            onClick={() => restoreTemplateMutation.mutate(serverTemplate.id)} disabled={restoreTemplateMutation.isLoading}
            icon={faTrashArrowUp}
            text="Restore Template"
          />}
        </Space>
      </Form.Item>
      <Form.Item>
        <ToggleCatalogBrowserButton disabled={isTemplateDeleted} />
      </Form.Item>
      <Form.Item>
        <TemplateItems />
      </Form.Item>
      <Form.Item label="Categories">
        <MultiSelect
          style={{ minWidth: 200, maxWidth: '100%' }}
          placeholder="Filter categories"
          selectedValues={state.categories ?? serverTemplate.categories}
          onChange={value => dispatch({ type: 'CATEGORIES_UPDATED', value })}
          maxTagCount={4}
          labelFieldName="name"
          valueFieldName="name"
          options={categoriesQuery.data ?? []}
          disabled={isTemplateDeleted}
        />
      </Form.Item>
      <Form.Item label="Workarea Description">
        <Tabs
          items={[
            {
              key: 'proposal',
              label: 'Proposal',
              children: <VSpace>
                <Text type="secondary">The work area summary shown on the customer proposal.</Text>
                <RichTextEditor value={customerNotes} onChange={value => dispatch({ type: 'WORKAREA_CUSTOMER_AND_INVOICE_NOTES_UPDATED', value })} disabled={isTemplateDeleted} />
              </VSpace>
            },
            {
              key: 'invoice',
              label: 'Invoice',
              forceRender: true,
              children: <VSpace>
                <Text type="secondary">The description new invoices for the work area start with. If it is empty, invoices use the proposal description.</Text>
                <RichTextEditor value={invoiceDescription} onChange={value => dispatch({ type: 'WORKAREA_INVOICE_DESCRIPTION_UPDATED', value })} disabled={isTemplateDeleted} />
              </VSpace>
            }
          ]}
        />
      </Form.Item>
      <Form.Item label="Workarea Internal Notes">
        <RichTextEditor value={internalNotes} onChange={value => dispatch({ type: 'WORKAREA_INTERNAL_NOTES_UPDATED', value })} disabled={isTemplateDeleted} />
      </Form.Item>
    </Form>
    <CopyTemplateModal
      isOpen={isCopyModalOpen}
      onCancel={() => setIsCopyModalOpen(false)}
      templateCopyName={`Copy of ${templateName}`}
      onCopy={name => {
        saveTemplateMutation.mutate({ id: 0, changeSet: copyTemplate(serverTemplate, name) })
        setIsCopyModalOpen(false)
      }} />
  </>
}

export default TemplateForm
